import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { DRIVERS_LIST, DRIVER_DELETE } from '../../graphql/drivers';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, CircularProgress, TextField, MenuItem, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const statusOptions = ['All', 'active', 'inactive'];

const DriverList = ({ onEdit }) => {
  const { data, loading, error, refetch } = useQuery(DRIVERS_LIST);
  const [deleteDriver] = useMutation(DRIVER_DELETE, {
    onCompleted: () => refetch(),
  });

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('All');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, driver: null });

  const handleDeleteClick = (driver) => {
    setDeleteDialog({ open: true, driver });
  };

  const handleDeleteConfirm = async () => {
    await deleteDriver({ variables: { id: deleteDialog.driver.id } });
    setDeleteDialog({ open: false, driver: null });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, driver: null });
  };

  const filteredDrivers = useMemo(() => {
    if (!data?.driversList?.items) return [];
    return data.driversList.items.filter((d) => {
      const matchesSearch =
        d.name.toLowerCase().includes(search.toLowerCase()) ||
        d.licenseNumber.toLowerCase().includes(search.toLowerCase()) ||
        d.phone.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = status === 'All' || d.status === status;
      return matchesSearch && matchesStatus;
    });
  }, [data, search, status]);

  if (loading) return <CircularProgress />;
  if (error) return <div>Error loading drivers.</div>;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, license number, or phone"
          size="small"
        />
        <TextField
          select
          label="Status"
          value={status}
          onChange={e => setStatus(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          {statusOptions.map(option => (
            <MenuItem key={option} value={option}>{option.charAt(0).toUpperCase() + option.slice(1)}</MenuItem>
          ))}
        </TextField>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>License Number</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDrivers.map((driver) => (
              <TableRow key={driver.id} hover onClick={() => onEdit(driver)}>
                <TableCell>{driver.name}</TableCell>
                <TableCell>{driver.licenseNumber}</TableCell>
                <TableCell>{driver.phone}</TableCell>
                <TableCell>{driver.status}</TableCell>
                <TableCell>
                  <IconButton onClick={e => { e.stopPropagation(); onEdit(driver); }} size="small" color="default" sx={{color: '#000'}}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={e => { e.stopPropagation(); handleDeleteClick(driver); }} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Driver</DialogTitle>
        <DialogContent>Are you sure you want to delete this driver?</DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriverList; 